import {Injectable} from '@angular/core';
import {AiChatUser} from '../models/ai-chat-user.model';
import {AiChatAccessState} from '../models/ai-chat-access.model';
import {AiChatAttachmentsAcceptedTypes} from '../models/ai-chat-attachment.model';
import {ChatMessagesPageState, ChatSessionsPageState} from '../cyn/chat-module/types/chat-session.types';

const STORAGE_KEY = 'ai-chat.session-state';
const STORAGE_VERSION = 1;

export type AiChatWindowAnchor = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

export interface AiChatWindowState {
  x: number;
  y: number;
  /** Угол вьюпорта, к которому привязано окно (по квадранту центра виджета). */
  anchor?: AiChatWindowAnchor;
  /** Отступ от вертикального края якоря (лево или право). */
  insetX?: number;
  /** Отступ от горизонтального края якоря (верх или низ). */
  insetY?: number;
}

export interface AiChatWindowSizeState {
  width: number;
  height: number;
}

export interface AiChatSessionState {
  user?: AiChatUser;
  sessionSignature?: string;
  /** Идентификатор вкладки для протокола чата: переживает reload, уникален для каждой вкладки. */
  tabId?: string;
  access?: AiChatAccessState;
  token?: string;
  attachmentsAcceptedTypes?: AiChatAttachmentsAcceptedTypes;
  ui?: {
    isOpen?: boolean;
    activeSessionId?: string | null;
    messagesScrollTop?: number | null;
    /** Текущая страница списка диалогов (`get_sessions` + курсорный стек). */
    sessionsPage?: ChatSessionsPageState | null;
    /** Текущая страница истории открытого диалога (`get_history` + before/after). */
    messagesPage?: ChatMessagesPageState | null;
  };
  window?: {
    position?: AiChatWindowState;
    size?: AiChatWindowSizeState;
  };
}

interface PersistedAiChatState {
  version: number;
  state: AiChatSessionState;
}

function createTabId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return Math.random().toString();
}

@Injectable({
  providedIn: 'root',
})
export class AiChatSessionStateStore {
  getState(): AiChatSessionState | null {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw) as PersistedAiChatState;
      if (!parsed || parsed.version !== STORAGE_VERSION || !parsed.state) {
        return null;
      }

      return parsed.state;
    } catch {
      return null;
    }
  }

  getOrCreateTabId(): string {
    const existingTabId = this.getState()?.tabId;
    if (existingTabId) {
      return existingTabId;
    }

    const tabId = createTabId();
    this.patchState({tabId});

    return tabId;
  }

  patchState(patch: Partial<AiChatSessionState>): void {
    const currentState = this.getState() || {};
    const nextState = this.mergeState(currentState, patch);
    this.saveState(nextState);
  }

  clear(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {}
  }

  private saveState(state: AiChatSessionState): void {
    try {
      const payload: PersistedAiChatState = {
        version: STORAGE_VERSION,
        state,
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {}
  }

  private mergeState(
    currentState: AiChatSessionState,
    patch: Partial<AiChatSessionState>,
  ): AiChatSessionState {
    return {
      ...currentState,
      ...patch,
      ui: {
        ...currentState.ui,
        ...patch.ui,
      },
      window: {
        ...currentState.window,
        ...patch.window,
      },
    };
  }
}
